import React, { useState, useRef, useEffect } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faClone, faCalendar, faDirections, faTrash, faTasks, faClock, faUtensils, faBed, faCompass, faCar, faMapMarkerAlt, faUmbrellaBeach } from '@fortawesome/free-solid-svg-icons';

// Add this function at the top of the file, before the styled components
const getPoiIcon = (type) => {
  switch (type) {
    case 'restaurant':
      return faUtensils;
    case 'hotel':
      return faBed;
    case 'exploration':
      return faCompass;
    case 'task':
      return faTasks;
    case 'rent_a_car':
      return faCar;
    case 'poi':
      return faUmbrellaBeach; // Add this case
    default:
      return faMapMarkerAlt;
  }
};


const POICard = styled.div`
  background-color: ${props => props.backgroundColor || '#57B2A6'};
  border: 2px solid ${props => props.backgroundColor || '#57B2A6'};
  border-radius: 4px;
  padding: 8px;
  cursor: grab;
  position: absolute;
  top: ${props => props.marginTop || '0'};
  left: ${props => `calc(${props.activityIndex * (100 / props.totalActivities)}%)`};
  width: ${props => `calc(${100 / props.totalActivities}% - 10px)`};
  z-index: ${props => props.isDragging ? 1000 : 1};
  pointer-events: auto;
  opacity: ${props => props.isDragging ? 0.7 : 1};
  overflow: visible;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  height: ${props => `${(props.duration || 1) * 60}px`};
  user-select: none;
  transform: ${props => props.isDragging ? 'rotate(2deg)' : 'none'};
  transition: box-shadow 0.3s cubic-bezier(.25,.8,.25,1);
`;

const POIContent = styled.div`
  display: flex;
  align-items: flex-start;
  position: relative;
  flex-direction: column;
  width: 100%;
`;

const POITitle = styled.h3`
  margin: 0;
  font-size: 0.9rem;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-left: 10px;
  flex-grow: 1;
  padding-right: 25px;
  max-width: calc(100% - 0px);
`;

const TimeInfo = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  color: white;
  margin-left: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 20px);
`;

const TimeText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 2px;
  font-size: 0.7rem;

  &:hover {
    color: #f0f0f0;
  }
`;

const DropdownMenu = styled.div`
  position: fixed;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  z-index: 1000;
  font-size: 0.8rem;
  width: 120px;
`;

const DropdownItem = styled.div`
  padding: 6px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  white-space: nowrap;
  
  &:hover {
    background-color: #f5f5f5;
  }

  svg {
    margin-right: 8px;
    font-size: 1rem;
  }
`;

const IconWrapper = styled.div`
  width: 2rem;
  height: 2rem;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StyledIcon = styled(FontAwesomeIcon)`
  color: white;
  font-size: 1rem;
`;

const EllipsisButton = styled(ActionButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  display: flex;
  justify-content: center;
  z-index: 2;
`;

const ResizablePOICard = ({ 
  poi, 
  index, 
  onResize, 
  onDelete, 
  onNavigate, 
  onClick, 
  onClone, 
  onCalendarClick, 
  backgroundColor, 
  icon, 
  children, 
  style,
  height,
  marginTop,
  totalActivities,
  activityIndex,
  itineraryId,
  isReadOnly
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const cardRef = useRef(null);
  const dropdownRef = useRef(null);
  const ellipsisRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  const handleEllipsisClick = (e) => {
    e.stopPropagation();
    const rect = ellipsisRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX
    });
    setShowDropdown(!showDropdown);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !ellipsisRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatTime = (hour) => {
    const date = new Date();
    date.setHours(hour);
    date.setMinutes(0);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const startTime = formatTime(poi.hour);
  const endTime = formatTime(poi.hour + poi.duration);

  const handleClick = () => {
    if (poi.isCustom) {
      onClick(poi, 'custom');
    } else {
      onClick(poi, 'poi');
    }
  };

  return (
    <>
      <Draggable 
        draggableId={poi.uniqueKey} 
        index={index}
        isDragDisabled={isReadOnly}
      >
        {(provided, snapshot) => (
          <POICard
            ref={(el) => {
              cardRef.current = el;
              provided.innerRef(el);
            }}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            onClick={handleClick}
            backgroundColor={backgroundColor}
            style={{
              ...provided.draggableProps.style,
              ...style,
            }}
            isDragging={snapshot.isDragging}
            totalActivities={totalActivities}
            activityIndex={activityIndex}
            marginTop={marginTop}
            duration={poi.duration || 1}
          >
            <POIContent>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                width: '100%',
                position: 'relative'
              }}>
                <IconWrapper>
                  <StyledIcon icon={getPoiIcon(poi.type)} />
                </IconWrapper>
                <div style={{ 
                  flex: 1,
                  minWidth: 0
                }}>
                  <POITitle>{poi.name}</POITitle>
                  <TimeInfo>
                    <FontAwesomeIcon 
                      icon={faClock} 
                      style={{ marginRight: '5px', fontSize: '0.7rem', flexShrink: 0 }} 
                    />
                    <TimeText>{startTime} - {endTime}</TimeText>
                  </TimeInfo>
                </div>
              </div>
            </POIContent>
            {children}
            {!isReadOnly && (
              <EllipsisButton onClick={handleEllipsisClick} ref={ellipsisRef}>
                <FontAwesomeIcon icon={faEllipsisV} />
              </EllipsisButton>
            )}
          </POICard>
        )}
      </Draggable>
      {showDropdown && (
        <DropdownMenu ref={dropdownRef} style={{ top: dropdownPosition.top, left: dropdownPosition.left }}>
          <DropdownItem onClick={(e) => { e.stopPropagation(); onClone(poi); setShowDropdown(false); }}>
            <FontAwesomeIcon icon={faClone} color="#4CAF50" /> Copy
          </DropdownItem>
          <DropdownItem onClick={(e) => { e.stopPropagation(); onNavigate(poi); setShowDropdown(false); }}>
            <FontAwesomeIcon icon={faDirections} color="#2196F3" /> Go now
          </DropdownItem>
          <DropdownItem onClick={(e) => { e.stopPropagation(); onDelete(poi.uniqueKey); setShowDropdown(false); }}>
            <FontAwesomeIcon icon={faTrash} color="#F44336" /> Remove
          </DropdownItem>
          <DropdownItem onClick={(e) => { e.stopPropagation(); onCalendarClick(poi); setShowDropdown(false); }}>
            <FontAwesomeIcon icon={faCalendar} color="#FF9800" /> Change day
          </DropdownItem>
        </DropdownMenu>
      )}
    </>
  );
};

export default ResizablePOICard;
